/**
 * KAN-846: шаг "Придумайте логин и пароль" экрана "Начало работы".
 * Показывает, чем менеджер будет входить (телефон из профиля), почту
 * (необязательно при телефоне, обязательно без него), пароль и повтор.
 * Логика и проверка в useMyCredentialsForm.
 */
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { ProfileField } from '@/components/mobile/ProfileField';
import { useMyCredentialsForm } from '@/hooks/useManagerCredentials';
import { ONBOARDING_TEXTS } from '@/lib/managerOnboarding';

const ID_PREFIX = 'credentials';

export function OnboardingCredentialsForm(): JSX.Element | null {
  const form = useMyCredentialsForm();
  const { draft, errors, setField, profile, phone } = form;
  if (!profile) return null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.save();
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3" data-testid="onboarding-credentials-form">
      <p className="text-sm text-gray-600">{ONBOARDING_TEXTS.credentialsNote}</p>
      {phone ? (
        <p className="text-sm text-gray-900" data-testid="credentials-login-phone">
          {ONBOARDING_TEXTS.credentialsLoginPhone}: <span className="font-medium">{phone}</span>
        </p>
      ) : (
        <p className="text-sm text-gray-600">{ONBOARDING_TEXTS.credentialsNoPhone}</p>
      )}
      <ProfileField
        idPrefix={ID_PREFIX}
        name="email"
        label={phone ? ONBOARDING_TEXTS.credentialsEmail : ONBOARDING_TEXTS.credentialsEmailRequired}
        type="email"
        inputMode="email"
        autoCapitalize="none"
        value={draft.email}
        onChange={(v) => setField('email', v)}
        error={errors.email}
        hint={phone ? ONBOARDING_TEXTS.credentialsEmailHint : undefined}
        autoComplete="email"
        maxLength={254}
      />
      <ProfileField
        idPrefix={ID_PREFIX}
        name="password"
        label={ONBOARDING_TEXTS.credentialsPassword}
        type="password"
        value={draft.password}
        onChange={(v) => setField('password', v)}
        error={errors.password}
        hint={ONBOARDING_TEXTS.credentialsPasswordHint}
        autoComplete="new-password"
        maxLength={128}
      />
      <ProfileField
        idPrefix={ID_PREFIX}
        name="password_confirm"
        label={ONBOARDING_TEXTS.credentialsConfirm}
        type="password"
        value={draft.password_confirm}
        onChange={(v) => setField('password_confirm', v)}
        error={errors.password_confirm}
        autoComplete="new-password"
        maxLength={128}
      />
      {form.formError && (
        <p role="alert" className="text-sm text-red-600">
          {form.formError}
        </p>
      )}
      <button
        type="submit"
        disabled={form.saving}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-medium text-white active:bg-blue-700 disabled:opacity-60"
      >
        {form.saving && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
        {ONBOARDING_TEXTS.credentialsSave}
      </button>
    </form>
  );
}
